import { useState } from "react";
import { COMPANY } from "@/data/site";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll be in touch shortly.", { duration: 4000 });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="section-y">
      <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5" data-testid="contact-details">
          <p className="eyebrow">Contact</p>
          <h1 className="font-serif text-5xl md:text-6xl text-primary mt-4 leading-[0.95]">
            Let&apos;s talk about <span className="italic text-secondary">your job</span>.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-md">
            Pick up the phone for urgent breakdowns or drop us a line — we&apos;ll respond fast.
          </p>

          <ul className="mt-12 space-y-6 text-primary">
            <li className="flex items-start gap-4">
              <MapPin className="mt-1 text-accent" size={20} />
              <div>
                <p className="eyebrow">Visit</p>
                <p className="mt-1">{COMPANY.address}</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Phone className="mt-1 text-accent" size={20} />
              <div>
                <p className="eyebrow">Phone</p>
                <a href={`tel:${COMPANY.phone.replace(/\s/g,'')}`} className="mt-1 block link-underline">{COMPANY.phone}</a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Mail className="mt-1 text-accent" size={20} />
              <div>
                <p className="eyebrow">Email</p>
                <a href={`mailto:${COMPANY.email}`} className="mt-1 block link-underline">{COMPANY.email}</a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Clock className="mt-1 text-accent" size={20} />
              <div>
                <p className="eyebrow">Hours</p>
                <p className="mt-1">{COMPANY.hours}</p>
              </div>
            </li>
          </ul>

          <div className="mt-10 aspect-[16/10] overflow-hidden border border-border">
            <iframe
              title="MIM Engineering location"
              src="https://maps.google.com/maps?q=10+Wright+Street+Bayswater+WA+6053&t=&z=15&ie=UTF8&iwloc=&output=embed"
              className="w-full h-full"
              loading="lazy"
            />
          </div>
        </div>

        <div className="lg:col-span-7 bg-white border border-border p-6 md:p-10">
          <h2 className="font-serif text-3xl md:text-4xl text-primary">Send a message</h2>
          <p className="text-muted-foreground mt-2">For detailed quotes use the <a className="link-underline text-primary" href="/quote">Request a Quote</a> form.</p>
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="c-name">Name</Label>
                <Input id="c-name" data-testid="contact-name" required value={form.name} onChange={on("name")} className="mt-2 rounded-none" />
              </div>
              <div>
                <Label htmlFor="c-email">Email</Label>
                <Input id="c-email" type="email" data-testid="contact-email" required value={form.email} onChange={on("email")} className="mt-2 rounded-none" />
              </div>
            </div>
            <div>
              <Label htmlFor="c-phone">Phone</Label>
              <Input id="c-phone" data-testid="contact-phone" value={form.phone} onChange={on("phone")} className="mt-2 rounded-none" />
            </div>
            <div>
              <Label htmlFor="c-message">Message</Label>
              <Textarea id="c-message" data-testid="contact-message" required rows={6} value={form.message} onChange={on("message")} className="mt-2 rounded-none" />
            </div>
            <Button type="submit" disabled={submitting} data-testid="contact-submit-btn" className="rounded-none uppercase tracking-[0.18em] text-xs px-6 py-3.5 h-auto">
              {submitting ? "Sending…" : "Send Message"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
